import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, LogOut, X, Package, ShoppingBag, FileText, TicketPercent } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (cents) => `$${(cents / 100).toFixed(2)}`;

const EMPTY_FORM = { name: "", tag: "", spec: "", description: "", price: "", sale_price: "", image: "", stock: "" };

const getPath = (obj, path) => path.split(".").reduce((o, k) => (o == null ? o : o[k]), obj);
const setPath = (obj, path, value) => {
  const keys = path.split(".");
  const clone = Array.isArray(obj) ? [...obj] : { ...obj };
  let cur = clone;
  for (let i = 0; i < keys.length - 1; i++) {
    const k = isNaN(Number(keys[i])) ? keys[i] : Number(keys[i]);
    cur[k] = Array.isArray(cur[k]) ? [...cur[k]] : { ...cur[k] };
    cur = cur[k];
  }
  const last = keys[keys.length - 1];
  cur[isNaN(Number(last)) ? last : Number(last)] = value;
  return clone;
};

const CONTENT_GROUPS = [
  { group: "Brand", fields: [["brand.name", "Shop / Brand Name"]] },
  {
    group: "Hero (top of page)",
    fields: [
      ["hero.headline", "Headline"],
      ["hero.subtext", "Subtext", "textarea"],
      ["hero.cta", "Button Label"],
      ["hero.note", "Note Next to Button"],
      ["hero.banner_overline", "Image Banner Overline"],
      ["hero.banner_text", "Image Banner Text", "textarea"],
    ],
  },
  {
    group: "Chapters (3 columns under hero)",
    fields: [0, 1, 2].flatMap((i) => [
      [`chapters.${i}.label`, `Chapter ${i + 1} Label`],
      [`chapters.${i}.title`, `Chapter ${i + 1} Title`],
      [`chapters.${i}.body`, `Chapter ${i + 1} Body`, "textarea"],
    ]),
  },
  {
    group: "Catalog Header",
    fields: [
      ["catalog.overline", "Overline"],
      ["catalog.heading", "Heading"],
      ["catalog.subtext", "Side Text", "textarea"],
    ],
  },
  {
    group: "Specs & Fit Section",
    fields: [
      ["materials.overline", "Overline"],
      ["materials.heading", "Heading"],
      ["materials.body", "Body Text", "textarea"],
      ["materials.fitment_title", "Fitment Table Title"],
      ...[0, 1, 2].flatMap((i) => [
        [`materials.specs.${i}.label`, `Spec ${i + 1} Label`],
        [`materials.specs.${i}.value`, `Spec ${i + 1} Value`],
      ]),
      ...[0, 1, 2, 3].flatMap((i) => [
        [`materials.fitment.${i}.model`, `Fitment Row ${i + 1} Model`],
        [`materials.fitment.${i}.fit`, `Fitment Row ${i + 1} Fit`],
        [`materials.fitment.${i}.note`, `Fitment Row ${i + 1} Note`],
      ]),
    ],
  },
  {
    group: "Plate Previewer",
    fields: [
      ["previewer.overline", "Overline"],
      ["previewer.heading", "Heading"],
      ["previewer.body", "Body Text", "textarea"],
    ],
  },
  {
    group: "Rider Reviews",
    fields: [
      ["reviews.overline", "Overline"],
      ["reviews.heading", "Heading"],
      ...[0, 1].flatMap((i) => [
        [`reviews.items.${i}.quote`, `Review ${i + 1} Quote`, "textarea"],
        [`reviews.items.${i}.name`, `Review ${i + 1} Name`],
        [`reviews.items.${i}.role`, `Review ${i + 1} Role`],
      ]),
      ["reviews.submit_title", "Submit Card Title"],
      ["reviews.submit_body", "Submit Card Text", "textarea"],
      ["reviews.submit_footer", "Submit Card Footer"],
    ],
  },
  {
    group: "FAQ",
    fields: [
      ["faq.overline", "Overline"],
      ["faq.heading", "Heading"],
      ...[0, 1, 2].flatMap((i) => [
        [`faq.items.${i}.q`, `Question ${i + 1}`],
        [`faq.items.${i}.a`, `Answer ${i + 1}`, "textarea"],
      ]),
    ],
  },
  {
    group: "Checkout Box",
    fields: [
      ["checkout.overline", "Overline"],
      ["checkout.heading", "Heading"],
      ["checkout.empty_cart", "Empty Cart Message"],
    ],
  },
  { group: "Footer", fields: [["footer.tagline", "Footer Tagline"]] },
];

function PromosTab() {
  const [promos, setPromos] = useState([]);
  const [code, setCode] = useState("");
  const [percent, setPercent] = useState("");
  const [expiresDays, setExpiresDays] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/promos`, { headers: authHeaders() });
      setPromos(data);
    } catch {
      toast.error("Failed to load codes");
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const create = async (e) => {
    e.preventDefault();
    const pct = parseInt(percent, 10);
    if (!code.trim() || !Number.isFinite(pct) || pct < 1 || pct > 90) {
      toast.error("Enter a code and a percent between 1 and 90");
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/admin/promos`, {
        code: code.trim(),
        percent: pct,
        expires_days: expiresDays === "" ? null : parseInt(expiresDays, 10),
      }, { headers: authHeaders() });
      toast.success(`Code ${code.trim().toUpperCase()} created`);
      setCode("");
      setPercent("");
      setExpiresDays("");
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not create code");
    } finally {
      setSaving(false);
    }
  };

  const toggle = async (c) => {
    try {
      await axios.put(`${API}/admin/promos/${c}/toggle`, {}, { headers: authHeaders() });
      await load();
    } catch {
      toast.error("Toggle failed");
    }
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete code ${c}?`)) return;
    try {
      await axios.delete(`${API}/admin/promos/${c}`, { headers: authHeaders() });
      toast.success("Code deleted");
      await load();
    } catch {
      toast.error("Delete failed");
    }
  };

  return (
    <div className="space-y-6" data-testid="promos-tab">
      <form onSubmit={create} className="bg-neutral-900 border border-neutral-800 rounded p-6 flex flex-col sm:flex-row gap-4 sm:items-end">
        <div className="flex-1">
          <label className="block text-xs font-mono uppercase text-neutral-400 mb-1">Code</label>
          <input
            data-testid="promo-code-input"
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            placeholder="e.g. GHOSTGANG10"
            maxLength={24}
            className="w-full bg-neutral-950 border border-neutral-800 rounded p-3 text-sm font-mono uppercase text-neutral-100 focus:border-white focus:outline-none"
          />
        </div>
        <div className="w-full sm:w-32">
          <label className="block text-xs font-mono uppercase text-neutral-400 mb-1">% Off</label>
          <input
            data-testid="promo-percent-input"
            type="number"
            min="1"
            max="90"
            value={percent}
            onChange={(e) => setPercent(e.target.value)}
            placeholder="10"
            className="w-full bg-neutral-950 border border-neutral-800 rounded p-3 text-sm text-neutral-100 focus:border-white focus:outline-none"
          />
        </div>
        <div className="w-full sm:w-44">
          <label className="block text-xs font-mono uppercase text-neutral-400 mb-1">Expires In (days, blank = never)</label>
          <input
            data-testid="promo-expires-input"
            type="number"
            min="1"
            value={expiresDays}
            onChange={(e) => setExpiresDays(e.target.value)}
            placeholder="7"
            className="w-full bg-neutral-950 border border-neutral-800 rounded p-3 text-sm text-neutral-100 focus:border-white focus:outline-none"
          />
        </div>
        <button
          data-testid="promo-create-button"
          type="submit"
          disabled={saving}
          className="px-6 py-3 bg-white hover:bg-neutral-200 disabled:opacity-50 text-neutral-950 font-bold uppercase tracking-wider text-sm rounded transition-colors"
        >
          {saving ? "Creating…" : "Create Code"}
        </button>
      </form>

      <div className="space-y-3" data-testid="promos-list">
        {promos.length === 0 && (
          <p className="text-xs font-mono uppercase tracking-widest text-neutral-500 border border-dashed border-neutral-800 rounded p-6 text-center">
            No codes yet — create one for your sponsors
          </p>
        )}
        {promos.map((p) => (
          <div key={p.code} data-testid={`promo-${p.code}`} className="flex items-center justify-between bg-neutral-900 border border-neutral-800 rounded p-4">
            <div className="flex items-center gap-4">
              <span className="font-mono font-bold text-white">{p.code}</span>
              <span className="text-xs font-mono text-neutral-400">{p.percent}% off</span>
              <span className="text-xs font-mono text-neutral-500">{p.uses || 0} used</span>
              <span data-testid={`promo-expiry-${p.code}`} className="text-xs font-mono text-neutral-500">
                {p.expires_at
                  ? new Date(p.expires_at) < new Date()
                    ? "Expired"
                    : `${Math.max(1, Math.ceil((new Date(p.expires_at) - new Date()) / 86400000))}d left`
                  : "Never expires"}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <button
                data-testid={`promo-toggle-${p.code}`}
                onClick={() => toggle(p.code)}
                className={`text-[10px] font-bold uppercase tracking-wider px-3 py-1.5 rounded transition-colors ${
                  p.active ? "bg-white text-neutral-950" : "bg-neutral-800 text-neutral-400 hover:bg-neutral-700"
                }`}
              >
                {p.active ? "Active" : "Off"}
              </button>
              <button
                data-testid={`promo-delete-${p.code}`}
                onClick={() => remove(p.code)}
                className="p-2 bg-neutral-800 hover:bg-red-500 hover:text-white rounded transition-colors"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ContentTab() {
  const [content, setContent] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/content`).then((r) => setContent(r.data)).catch(() => toast.error("Failed to load content"));
  }, []);

  if (!content) return <p className="text-xs font-mono text-neutral-500 uppercase tracking-widest">Loading content…</p>;

  const update = (path) => (e) => setContent((prev) => setPath(prev, path, e.target.value));

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/content`, content, { headers: authHeaders() });
      toast.success("Site content saved — live on the store now");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-8" data-testid="content-tab">
      {CONTENT_GROUPS.map((g) => (
        <div key={g.group} className="bg-neutral-900 border border-neutral-800 rounded p-6">
          <h3 className="font-disp text-lg uppercase text-white mb-4">{g.group}</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {g.fields.map(([path, label, type]) => (
              <div key={path} className={type === "textarea" ? "sm:col-span-2" : ""}>
                <label className="block text-xs font-mono uppercase text-neutral-400 mb-1">{label}</label>
                {type === "textarea" ? (
                  <textarea
                    data-testid={`content-${path}`}
                    rows={2}
                    value={getPath(content, path) ?? ""}
                    onChange={update(path)}
                    className="w-full bg-neutral-950 border border-neutral-800 rounded p-3 text-sm text-neutral-100 focus:border-white focus:outline-none"
                  />
                ) : (
                  <input
                    data-testid={`content-${path}`}
                    value={getPath(content, path) ?? ""}
                    onChange={update(path)}
                    className="w-full bg-neutral-950 border border-neutral-800 rounded p-3 text-sm text-neutral-100 focus:border-white focus:outline-none"
                  />
                )}
              </div>
            ))}
          </div>
        </div>
      ))}
      <button
        data-testid="content-save-button"
        onClick={save}
        disabled={saving}
        className="px-8 py-3.5 bg-white hover:bg-neutral-200 disabled:opacity-50 text-neutral-950 font-bold uppercase tracking-wider text-sm rounded transition-colors"
      >
        {saving ? "Saving…" : "Save All Site Content"}
      </button>
    </div>
  );
}

function authHeaders() {
  return { Authorization: `Bearer ${localStorage.getItem("vf_admin_token") || ""}` };
}

function Login({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/admin/login`, { email, password });
      localStorage.setItem("vf_admin_token", data.token);
      onLogin();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-neutral-950 grid-pattern flex items-center justify-center px-4">
      <form
        data-testid="admin-login-form"
        onSubmit={submit}
        className="w-full max-w-sm bg-neutral-900 border border-neutral-800 rounded-lg p-8 space-y-5"
      >
        <div className="flex items-center gap-3">
          <div className="w-3.5 h-3.5 bg-white rounded shadow-[0_0_12px_rgba(255,255,255,0.6)]" />
          <span className="text-sm font-bold tracking-wider uppercase text-neutral-100">Gridline Admin</span>
        </div>
        <div>
          <label htmlFor="admin-email" className="block text-xs font-mono uppercase text-neutral-400 mb-1">Username</label>
          <input
            id="admin-email"
            data-testid="admin-email-input"
            type="text"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full bg-neutral-950 border border-neutral-800 rounded p-3 text-sm text-neutral-100 focus:border-white focus:outline-none"
          />
        </div>
        <div>
          <label htmlFor="admin-password" className="block text-xs font-mono uppercase text-neutral-400 mb-1">Password</label>
          <input
            id="admin-password"
            data-testid="admin-password-input"
            type="password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full bg-neutral-950 border border-neutral-800 rounded p-3 text-sm text-neutral-100 focus:border-white focus:outline-none"
          />
        </div>
        <button
          data-testid="admin-login-button"
          type="submit"
          disabled={loading}
          className="w-full py-3 bg-white hover:bg-neutral-200 disabled:opacity-50 text-neutral-950 font-bold uppercase tracking-wider text-sm rounded transition-colors"
        >
          {loading ? "Signing in…" : "Sign In"}
        </button>
      </form>
    </div>
  );
}

function ProductForm({ initial, onSave, onCancel, saving }) {
  const [form, setForm] = useState(initial);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <form
      data-testid="product-form"
      onSubmit={(e) => {
        e.preventDefault();
        onSave(form);
      }}
      className="bg-neutral-900 border border-white/40 rounded-lg p-6 space-y-4"
    >
      <div className="flex items-center justify-between">
        <h3 className="font-disp text-xl uppercase text-white">{initial.product_id ? "Edit Product" : "New Product"}</h3>
        <button type="button" data-testid="product-form-cancel" onClick={onCancel} className="text-neutral-400 hover:text-white">
          <X className="w-5 h-5" />
        </button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="sm:col-span-2">
          <label className="block text-xs font-mono uppercase text-neutral-400 mb-1">Name</label>
          <input data-testid="product-form-name" required value={form.name} onChange={set("name")}
            className="w-full bg-neutral-950 border border-neutral-800 rounded p-3 text-sm text-neutral-100 focus:border-white focus:outline-none" />
        </div>
        <div>
          <label className="block text-xs font-mono uppercase text-neutral-400 mb-1">Tag (e.g. Stock Fit)</label>
          <input data-testid="product-form-tag" value={form.tag} onChange={set("tag")}
            className="w-full bg-neutral-950 border border-neutral-800 rounded p-3 text-sm text-neutral-100 focus:border-white focus:outline-none" />
        </div>
        <div>
          <label className="block text-xs font-mono uppercase text-neutral-400 mb-1">Spec Line</label>
          <input data-testid="product-form-spec" value={form.spec} onChange={set("spec")}
            className="w-full bg-neutral-950 border border-neutral-800 rounded p-3 text-sm text-neutral-100 focus:border-white focus:outline-none" />
        </div>
        <div className="sm:col-span-2">
          <label className="block text-xs font-mono uppercase text-neutral-400 mb-1">Description</label>
          <textarea data-testid="product-form-description" rows={3} value={form.description} onChange={set("description")}
            className="w-full bg-neutral-950 border border-neutral-800 rounded p-3 text-sm text-neutral-100 focus:border-white focus:outline-none" />
        </div>
        <div>
          <label className="block text-xs font-mono uppercase text-neutral-400 mb-1">Price (USD)</label>
          <input data-testid="product-form-price" required type="number" min="0.5" step="0.01" value={form.price} onChange={set("price")}
            placeholder="48.00"
            className="w-full bg-neutral-950 border border-neutral-800 rounded p-3 text-sm text-neutral-100 focus:border-white focus:outline-none" />
        </div>
        <div>
          <label className="block text-xs font-mono uppercase text-neutral-400 mb-1">Sale Price (USD, blank = no sale)</label>
          <input data-testid="product-form-sale-price" type="number" min="0.5" step="0.01" value={form.sale_price} onChange={set("sale_price")}
            placeholder="80.00"
            className="w-full bg-neutral-950 border border-neutral-800 rounded p-3 text-sm text-neutral-100 focus:border-white focus:outline-none" />
        </div>
        <div>
          <label className="block text-xs font-mono uppercase text-neutral-400 mb-1">Stock (blank = unlimited)</label>
          <input data-testid="product-form-stock" type="number" min="0" step="1" value={form.stock} onChange={set("stock")}
            placeholder="∞"
            className="w-full bg-neutral-950 border border-neutral-800 rounded p-3 text-sm text-neutral-100 focus:border-white focus:outline-none" />
        </div>
        <div className="sm:col-span-2">
          <label className="block text-xs font-mono uppercase text-neutral-400 mb-1">Photo URL</label>
          <input data-testid="product-form-image" value={form.image} onChange={set("image")}
            placeholder="https://…"
            className="w-full bg-neutral-950 border border-neutral-800 rounded p-3 text-sm text-neutral-100 focus:border-white focus:outline-none" />
        </div>
      </div>
      {form.image && (
        <img src={form.image} alt="preview" className="h-24 w-24 object-cover rounded border border-neutral-800" />
      )}
      <button
        data-testid="product-form-save"
        type="submit"
        disabled={saving}
        className="px-6 py-3 bg-white hover:bg-neutral-200 disabled:opacity-50 text-neutral-950 font-bold uppercase tracking-wider text-sm rounded transition-colors"
      >
        {saving ? "Saving…" : "Save Product"}
      </button>
    </form>
  );
}

function Dashboard({ onLogout }) {
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [tab, setTab] = useState("products");
  const [editing, setEditing] = useState(null); // null | EMPTY_FORM | product
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const [p, o] = await Promise.all([
        axios.get(`${API}/admin/products`, { headers: authHeaders() }),
        axios.get(`${API}/admin/orders`, { headers: authHeaders() }),
      ]);
      setProducts(p.data);
      setOrders(o.data);
    } catch (err) {
      if (err.response?.status === 401) onLogout();
      else toast.error("Failed to load admin data");
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const save = async (form) => {
    const priceCents = Math.round(parseFloat(form.price) * 100);
    if (!Number.isFinite(priceCents) || priceCents < 50) {
      toast.error("Enter a valid price (min $0.50)");
      return;
    }
    setSaving(true);
    const saleCents = form.sale_price === "" ? null : Math.round(parseFloat(form.sale_price) * 100);
    if (saleCents !== null && (!Number.isFinite(saleCents) || saleCents < 50)) {
      toast.error("Sale price must be at least $0.50 (or leave blank)");
      return;
    }
    if (saleCents !== null && saleCents >= priceCents) {
      toast.error("Sale price must be lower than the regular price");
      return;
    }
    const payload = {
      name: form.name,
      tag: form.tag,
      spec: form.spec,
      description: form.description,
      price_cents: priceCents,
      sale_price_cents: saleCents,
      image: form.image,
      stock: form.stock === "" || form.stock === null || form.stock === undefined ? null : Math.max(0, parseInt(form.stock, 10) || 0),
    };
    try {
      if (form.product_id) {
        await axios.put(`${API}/admin/products/${form.product_id}`, payload, { headers: authHeaders() });
        toast.success("Product updated");
      } else {
        await axios.post(`${API}/admin/products`, payload, { headers: authHeaders() });
        toast.success("Product added");
      }
      setEditing(null);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (productId) => {
    if (!window.confirm("Remove this product from the store?")) return;
    try {
      await axios.delete(`${API}/admin/products/${productId}`, { headers: authHeaders() });
      toast.success("Product removed");
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Delete failed");
    }
  };

  const setFulfillment = async (orderId, status) => {
    try {
      const { data } = await axios.put(`${API}/admin/orders/${orderId}/fulfillment`, { status }, { headers: authHeaders() });
      toast.success(`Order marked ${status}${data.email_sent ? " — buyer emailed" : ""}`);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Status update failed");
    }
  };

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-neutral-950 text-neutral-100">
      <header className="border-b border-neutral-800 bg-neutral-950/90 sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-3.5 h-3.5 bg-white rounded shadow-[0_0_12px_rgba(255,255,255,0.6)]" />
            <span className="text-sm font-bold tracking-wider uppercase">Gridline Admin</span>
          </div>
          <div className="flex items-center gap-3">
            <a href="/" data-testid="admin-view-store" className="text-xs uppercase tracking-wider text-neutral-400 hover:text-white transition-colors">
              View Store
            </a>
            <button
              data-testid="admin-logout-button"
              onClick={onLogout}
              className="flex items-center gap-2 text-xs uppercase tracking-wider font-bold bg-neutral-800 hover:bg-neutral-700 px-4 py-2 rounded transition-colors"
            >
              <LogOut className="w-4 h-4" /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-10 space-y-8">
        <div className="flex items-center gap-3">
          <button
            data-testid="tab-products"
            onClick={() => setTab("products")}
            className={`flex items-center gap-2 px-4 py-2 rounded text-xs font-bold uppercase tracking-wider transition-colors ${
              tab === "products" ? "bg-white text-neutral-950" : "bg-neutral-800 text-neutral-300 hover:bg-neutral-700"
            }`}
          >
            <Package className="w-4 h-4" /> Products ({products.length})
          </button>
          <button
            data-testid="tab-orders"
            onClick={() => setTab("orders")}
            className={`flex items-center gap-2 px-4 py-2 rounded text-xs font-bold uppercase tracking-wider transition-colors ${
              tab === "orders" ? "bg-white text-neutral-950" : "bg-neutral-800 text-neutral-300 hover:bg-neutral-700"
            }`}
          >
            <ShoppingBag className="w-4 h-4" /> Orders ({orders.length})
          </button>
          <button
            data-testid="tab-content"
            onClick={() => setTab("content")}
            className={`flex items-center gap-2 px-4 py-2 rounded text-xs font-bold uppercase tracking-wider transition-colors ${
              tab === "content" ? "bg-white text-neutral-950" : "bg-neutral-800 text-neutral-300 hover:bg-neutral-700"
            }`}
          >
            <FileText className="w-4 h-4" /> Site Content
          </button>
          <button
            data-testid="tab-promos"
            onClick={() => setTab("promos")}
            className={`flex items-center gap-2 px-4 py-2 rounded text-xs font-bold uppercase tracking-wider transition-colors ${
              tab === "promos" ? "bg-white text-neutral-950" : "bg-neutral-800 text-neutral-300 hover:bg-neutral-700"
            }`}
          >
            <TicketPercent className="w-4 h-4" /> Codes
          </button>
        </div>

        {tab === "content" && <ContentTab />}
        {tab === "promos" && <PromosTab />}

        {tab === "products" && (
          <div className="space-y-6">
            {!editing && (
              <button
                data-testid="add-product-button"
                onClick={() => setEditing({ ...EMPTY_FORM })}
                className="flex items-center gap-2 px-5 py-3 bg-white hover:bg-neutral-200 text-neutral-950 font-bold uppercase tracking-wider text-sm rounded transition-colors"
              >
                <Plus className="w-4 h-4" /> Add Product
              </button>
            )}
            {editing && (
              <ProductForm
                initial={editing}
                saving={saving}
                onSave={save}
                onCancel={() => setEditing(null)}
              />
            )}
            <div className="grid grid-cols-1 gap-3">
              {products.map((p) => (
                <div
                  key={p.product_id}
                  data-testid={`admin-product-${p.product_id}`}
                  className="flex items-center gap-4 bg-neutral-900 border border-neutral-800 rounded p-4"
                >
                  {p.image ? (
                    <img src={p.image} alt={p.name} className="w-16 h-16 object-cover rounded border border-neutral-800" />
                  ) : (
                    <div className="w-16 h-16 rounded border border-neutral-800 bg-neutral-950" />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className="font-bold text-sm text-neutral-100 truncate">{p.name}</h3>
                      {p.tag && <span className="text-[10px] font-mono text-white uppercase">{p.tag}</span>}
                    </div>
                    <p className="text-xs text-neutral-400 truncate mt-0.5">{p.description}</p>
                    <div className="flex items-center gap-3 mt-1">
                      <span className="text-sm font-mono font-bold text-white">
                        {p.sale_price_cents && p.sale_price_cents < p.price_cents ? (
                          <>
                            <span className="text-neutral-500 line-through text-xs mr-2 font-normal">{fmt(p.price_cents)}</span>
                            {fmt(p.sale_price_cents)}
                            <span className="ml-2 text-[10px] uppercase px-1.5 py-0.5 bg-white text-neutral-950 rounded">Sale</span>
                          </>
                        ) : (
                          fmt(p.price_cents)
                        )}
                      </span>
                      <span data-testid={`admin-stock-${p.product_id}`} className="text-[10px] font-mono uppercase text-neutral-500">
                        {p.stock === null || p.stock === undefined ? "Unlimited stock" : p.stock === 0 ? "Sold out" : `${p.stock} in stock`}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      data-testid={`edit-product-${p.product_id}`}
                      onClick={() => setEditing({ ...p, price: (p.price_cents / 100).toFixed(2), sale_price: p.sale_price_cents ? (p.sale_price_cents / 100).toFixed(2) : "", stock: p.stock === null || p.stock === undefined ? "" : String(p.stock) })}
                      className="p-2 bg-neutral-800 hover:bg-white hover:text-neutral-950 rounded transition-colors"
                    >
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button
                      data-testid={`delete-product-${p.product_id}`}
                      onClick={() => remove(p.product_id)}
                      className="p-2 bg-neutral-800 hover:bg-red-500 hover:text-white rounded transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "orders" && (
          <div className="space-y-3" data-testid="orders-list">
            {orders.length === 0 && (
              <p className="text-xs font-mono uppercase tracking-widest text-neutral-500 border border-dashed border-neutral-800 rounded p-6 text-center">
                No orders yet
              </p>
            )}
            {orders.map((o) => (
              <div key={o.session_id} data-testid={`order-${o.order_id}`} className="bg-neutral-900 border border-neutral-800 rounded p-4">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="font-mono text-xs text-neutral-400">#{o.order_id.slice(0, 8)}</span>
                  <span
                    className={`text-[10px] font-mono uppercase px-2 py-0.5 rounded border ${
                      o.payment_status === "paid"
                        ? "text-white border-white/40 bg-white/10"
                        : "text-neutral-400 border-neutral-700"
                    }`}
                  >
                    {o.payment_status}
                  </span>
                </div>
                <div className="mt-2 text-sm text-neutral-200">
                  {(o.items || []).map((i) => `${i.name} ×${i.quantity}`).join(", ")}
                </div>
                <div className="mt-1 flex flex-wrap items-center gap-4 text-xs text-neutral-400 font-mono">
                  <span className="text-white font-bold">{fmt(o.amount)}</span>
                  {o.promo_code && <span className="text-white">code {o.promo_code} −{o.promo_percent}%</span>}
                  {o.email && <span>{o.email}</span>}
                  {o.custom_details && <span className="text-neutral-300">{o.custom_details}</span>}
                </div>
                <div className="mt-3 pt-3 border-t border-neutral-800 flex items-center gap-2">
                  <span className="text-[10px] font-mono uppercase text-neutral-500 mr-1">Fulfillment:</span>
                  {["processing", "shipped", "delivered"].map((s) => (
                    <button
                      key={s}
                      data-testid={`order-${o.order_id}-status-${s}`}
                      onClick={() => setFulfillment(o.order_id, s)}
                      className={`text-[10px] font-bold uppercase tracking-wider px-3 py-1.5 rounded transition-colors ${
                        (o.fulfillment || "processing") === s
                          ? "bg-white text-neutral-950"
                          : "bg-neutral-800 text-neutral-400 hover:bg-neutral-700"
                      }`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}

export default function Admin() {
  const [authed, setAuthed] = useState(!!localStorage.getItem("vf_admin_token"));

  useEffect(() => {
    document.title = "Gridline Admin";
  }, []);

  const logout = () => {
    localStorage.removeItem("vf_admin_token");
    setAuthed(false);
  };

  return authed ? <Dashboard onLogout={logout} /> : <Login onLogin={() => setAuthed(true)} />;
}
